import { type ThreadId } from "@cmmd-forge/contracts";
import { deriveActiveWorkStartedAt, derivePhase } from "../../session-logic";
import { useChatActivityViewState } from "./useChatActivityViewState";
import {
  useApexDisconnectToast,
  useMarkSettledThreadVisited,
  useServerThreadSubscription,
} from "./useChatThreadLifecycle";
import { useChatProjectContext } from "./useChatProjectContext";
import { useChatTimelineMessages } from "./useChatTimelineMessages";
import { useChatTimelineDerivedState } from "./useChatTimelineDerivedState";
import { useLocalDispatchState } from "./useLocalDispatchState";
import { usePendingUserInputControls } from "./usePendingUserInputControls";
import { useProviderResponseControls } from "./useProviderResponseControls";
import { type useChatRouteRuntime } from "./useChatRouteRuntime";

export function useChatThreadRuntimeState(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadId: ThreadId;
  readonly isConnecting: boolean;
  readonly isRevertingCheckpoint: boolean;
}) {
  const providerResponses = useChatProviderResponses(input.routeRuntime);
  const projectContext = useChatProjectRuntimeContext(input.routeRuntime);
  useChatThreadRuntimeLifecycle({
    routeRuntime: input.routeRuntime,
    projectContext,
  });
  const activity = useChatThreadActivity({
    routeRuntime: input.routeRuntime,
    projectContext,
  });
  const pendingInput = useChatPendingInputRuntime({
    routeRuntime: input.routeRuntime,
    activity,
    providerResponses,
  });
  const dispatch = useChatDispatchRuntime({
    routeRuntime: input.routeRuntime,
    projectContext,
    activity,
    pendingInput,
  });
  const work = useChatWorkRuntime({
    input,
    projectContext,
    activity,
    dispatch,
  });
  const timeline = useChatTimelineRuntime({
    routeRuntime: input.routeRuntime,
    threadId: input.threadId,
    projectContext,
    activity,
    dispatch,
  });

  return {
    providerResponses,
    projectContext,
    activity,
    pendingInput,
    dispatch,
    work,
    timeline,
  };
}

function useChatProviderResponses(routeRuntime: ReturnType<typeof useChatRouteRuntime>) {
  const { environmentId } = routeRuntime.route;
  const { activeThreadId, setThreadError } = routeRuntime.bootstrap;
  return useProviderResponseControls({
    environmentId,
    activeThreadId,
    setThreadError,
  });
}

function useChatProjectRuntimeContext(routeRuntime: ReturnType<typeof useChatRouteRuntime>) {
  const { activeThread, activeThreadRef, activeThreadEnvironmentId, activeThreadProjectId } =
    routeRuntime.bootstrap;
  return useChatProjectContext({
    activeThread,
    activeThreadRef,
    activeThreadEnvironmentId,
    activeThreadProjectId,
  });
}

function useChatThreadRuntimeLifecycle(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly projectContext: ReturnType<typeof useChatProjectContext>;
}) {
  const { environmentId, threadId, routeKind } = input.routeRuntime.route;
  const { serverThread, activeThread, activeThreadLastVisitedAt, markThreadVisited } =
    input.routeRuntime.bootstrap;
  const { activeLatestTurn, latestTurnSettled } = input.projectContext;
  useServerThreadSubscription({ routeKind, environmentId, threadId });
  useMarkSettledThreadVisited({
    serverThread,
    activeLatestTurn,
    latestTurnSettled,
    activeThreadLastVisitedAt,
    markThreadVisited,
  });
  useApexDisconnectToast({ activeThread, activeLatestTurn, latestTurnSettled });
}

function useChatThreadActivity(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly projectContext: ReturnType<typeof useChatProjectContext>;
}) {
  const { activeThread, interactionMode } = input.routeRuntime.bootstrap;
  const { activeLatestTurn, latestTurnSettled, threadPlanCatalog } = input.projectContext;
  const phase = derivePhase(activeThread?.session ?? null);
  return {
    phase,
    ...useChatActivityViewState({
      activeThread,
      activeLatestTurn,
      latestTurnSettled,
      interactionMode,
      phase,
      threadPlanCatalog,
    }),
  };
}

function useChatPendingInputRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly activity: ReturnType<typeof useChatThreadActivity>;
  readonly providerResponses: ReturnType<typeof useProviderResponseControls>;
}) {
  return usePendingUserInputControls({
    environmentId: input.routeRuntime.route.environmentId,
    threadId: input.routeRuntime.route.threadId,
    pendingUserInputs: input.activity.pendingUserInputs,
    respondingUserInputRequestIds: input.providerResponses.respondingUserInputRequestIds,
    promptRef: input.routeRuntime.composer.promptRef,
    composerRef: input.routeRuntime.composer.composerRef,
    onRespondToUserInput: input.providerResponses.onRespondToUserInput,
  });
}

function useChatDispatchRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly projectContext: ReturnType<typeof useChatProjectContext>;
  readonly activity: ReturnType<typeof useChatThreadActivity>;
  readonly pendingInput: ReturnType<typeof usePendingUserInputControls>;
}) {
  const { activeThread } = input.routeRuntime.bootstrap;
  return useLocalDispatchState({
    activeThread,
    activeLatestTurn: input.projectContext.activeLatestTurn,
    phase: input.activity.phase,
    activePendingApproval: input.activity.activePendingApproval?.requestId ?? null,
    activePendingUserInput: input.pendingInput.activePendingUserInput?.requestId ?? null,
    threadError: activeThread?.error,
  });
}

function useChatWorkRuntime(input: {
  readonly input: {
    readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
    readonly isConnecting: boolean;
    readonly isRevertingCheckpoint: boolean;
  };
  readonly projectContext: ReturnType<typeof useChatProjectContext>;
  readonly activity: ReturnType<typeof useChatThreadActivity>;
  readonly dispatch: ReturnType<typeof useLocalDispatchState>;
}) {
  const { activeThread } = input.input.routeRuntime.bootstrap;
  const isWorking =
    input.activity.phase === "running" ||
    input.dispatch.isSendBusy ||
    input.input.isConnecting ||
    input.input.isRevertingCheckpoint;
  const activeWorkStartedAt = deriveActiveWorkStartedAt(
    input.projectContext.activeLatestTurn,
    activeThread?.session ?? null,
    input.dispatch.localDispatchStartedAt,
  );
  return { isWorking, activeWorkStartedAt };
}

function useChatTimelineRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadId: ThreadId;
  readonly projectContext: ReturnType<typeof useChatProjectContext>;
  readonly activity: ReturnType<typeof useChatThreadActivity>;
  readonly dispatch: ReturnType<typeof useLocalDispatchState>;
}) {
  const { activeThread } = input.routeRuntime.bootstrap;
  const { setOptimisticUserMessages, timelineMessages } = useChatTimelineMessages({
    activeThread,
    draftId: input.routeRuntime.route.draftId,
    threadId: input.threadId,
    stagedSubmission: input.routeRuntime.bootstrap.composerStagedSubmission,
    resetLocalDispatch: input.dispatch.resetLocalDispatch,
    setExpandedImage: input.routeRuntime.imagePreview.setExpandedImage,
  });
  return {
    setOptimisticUserMessages,
    timelineMessages,
    ...useChatTimelineDerivedState({
      activeThread,
      activeLatestTurn: input.projectContext.activeLatestTurn,
      latestTurnSettled: input.projectContext.latestTurnSettled,
      latestTurnHasToolActivity: input.activity.latestTurnHasToolActivity,
      timelineMessages,
      workLogEntries: input.activity.workLogEntries,
      inferredPhase: input.activity.phase,
    }),
  };
}
